import styled from '@emotion/styled';
import { motion, LayoutGroup } from 'framer-motion';
import { useEffect, useState } from 'react';
import tinycolor from 'tinycolor2';

import {
  getColorForTemperature,
  getColorForHueSaturation,
  getMode,
  hasColorMode,
  hasTemperatureMode,
} from '../../../lib/capabilities/light';
import { logScale } from '../../../lib/math';
import * as Containers from '../Containers';
import { setBackgroundAnimationColor } from '../ControlsBackground';

import { useCapabilityValues } from '../../../store/devices/useCapabilityValues';
import { useDevice } from '../../../store/devices/useDevices';

import { su } from '../../../theme/functions/su';

import { ColorRadial } from './ColorRadial';
import { ModeButton } from './ModeButton';
import { Picker } from './Picker';
import { TemperatureRadial } from './TemperatureRadial';

export function Color(props) {
  const { device } = useDevice({ deviceId: props.deviceId });

  const [isRadialMode, setIsRadialMode] = useState(false);
  const [x, setX] = useState(0);

  const [values, setValues] = useCapabilityValues({
    deviceId: props.deviceId,
    capabilityIds: [
      'light_temperature',
      'light_hue',
      'light_saturation',
      'light_mode',
      'dim',
      'onoff',
    ],
  });

  function handleColorChange({ hue, saturation }) {
    setValues({
      light_hue: hue / 360,
      light_saturation: saturation,
    });
  }

  function handleTemperatureChange({ temperature, x }) {
    setX(x);
    setValues({
      light_temperature: temperature,
    });
  }

  function handleModeClick(mode) {
    const prevMode = getMode(values);
    const prevIsRadialMode = isRadialMode;

    const nextIsRadialMode = mode === prevMode ? !prevIsRadialMode : prevIsRadialMode;
    setIsRadialMode(nextIsRadialMode);

    if (device.capabilitiesObj.light_mode != null) {
      setValues({
        light_mode: mode,
      });
    }
  }

  const currentMode = getMode(values);
  const color = getColor({ values, mode: getMode(values) });

  useEffect(() => {
    if (color) {
      const start = tinycolor.mix(color, '#262626', 30).toHexString();
      const stop = tinycolor.mix(color, '#131313', 60).toHexString();
      const angle = 180;
      let isOn = values.onoff ?? true;

      if (isOn && values.dim === 0) {
        isOn = false;
      }

      setBackgroundAnimationColor({
        start,
        stop,
        angle,
        opacity: isOn ? logScale(values.dim ?? 1, 0.4, 0.8) : 0,
      });
    }
  }, [color, values.dim, values.onoff]);

  const light_modes = [];

  if (hasColorMode(values)) {
    light_modes.push('color');
  }

  if (hasTemperatureMode(values)) {
    light_modes.push('temperature');
  }

  return (
    <Containers.Control>
      <Containers.Select />
      <Containers.Action>
        <LayoutGroup>
          <Color.Root>
            <Color.Picker>
              {isRadialMode === true && currentMode === 'color' && (
                <ColorRadial
                  hue={values.light_hue}
                  saturation={values.light_saturation}
                  onChange={handleColorChange}
                />
              )}

              {isRadialMode === true && currentMode === 'temperature' && (
                <TemperatureRadial
                  temperature={values.light_temperature}
                  x={x}
                  onChange={handleTemperatureChange}
                />
              )}

              {isRadialMode === false && currentMode === 'color' && (
                <Picker
                  mode={currentMode}
                  color={color}
                  onSelect={(color) => {
                    const hsv = tinycolor(color).toHsv();
                    handleColorChange({ hue: hsv.h, saturation: hsv.s });
                  }}
                />
              )}

              {isRadialMode === false && currentMode === 'temperature' && (
                <Picker
                  mode={currentMode}
                  color={color}
                  onSelect={(temperature) => {
                    handleTemperatureChange({ temperature: temperature, x: 0 });
                  }}
                />
              )}
            </Color.Picker>

            <Color.ButtonContainer>
              {light_modes.map((mode) => {
                return (
                  <ModeButton
                    key={mode}
                    mode={mode}
                    size={46}
                    onPress={() => handleModeClick(mode)}
                  >
                    {mode === currentMode && (
                      <Color.ModeButtonOutline
                        layoutId="outline"
                        initial={false}
                        //animate={{ borderColor: 'white' }}
                        transition={spring}
                      />
                    )}
                  </ModeButton>
                );
              })}
            </Color.ButtonContainer>
          </Color.Root>
        </LayoutGroup>
      </Containers.Action>
    </Containers.Control>
  );
}

function getColor({ values, mode }) {
  if (mode === 'color') {
    return getColorForHueSaturation({
      light_hue: values.light_hue,
      light_saturation: values.light_saturation,
    }).toHexString();
  }

  if (mode === 'temperature') {
    return getColorForTemperature({
      light_temperature: values.light_temperature,
    }).toHexString();
  }
}

const spring = {
  type: 'spring',
  stiffness: 500,
  damping: 30,
};

Color.Root = styled.div``;

Color.Picker = styled.div`
  margin-bottom: ${su(5)};
`;

Color.ModeButtonOutline = styled(motion.div)`
  position: absolute;
  top: -4px;
  right: -4px;
  bottom: -4px;
  left: -4px;
  border: 2px solid white;
  border-radius: 50%;
`;

Color.ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: ${su(5)};
`;
